import { useEffect, useState } from 'react'
import { PencilSquare, Trash3, X } from 'react-bootstrap-icons'
import { money } from '../lib/format.js'

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      {children && <div className="d-flex gap-2 flex-wrap">{children}</div>}
    </div>
  )
}

export function Stat({ label, value, hint, tone }) {
  return (
    <div className="stat h-100">
      <div className="label">{label}</div>
      <div className={`value ${tone || ''}`}>{value}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  )
}

export function Panel({ title, action, children, className = '' }) {
  return (
    <div className={`panel ${className}`}>
      {(title || action) && <div className="panel-title"><span>{title}</span>{action}</div>}
      {children}
    </div>
  )
}

export function Modal({ title, onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div className="modal d-block" tabIndex={-1} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  )
}

/**
 * Generic form modal. fields: [{ name, label, type: text|number|date|select|checkbox|textarea, options, required, step, col }]
 */
export function FormModal({ title, fields, initial, onSubmit, onClose, submitLabel = 'Save' }) {
  const [values, setValues] = useState(initial || {})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const set = (name, v) => setValues((s) => {
    const next = { ...s, [name]: v }
    // Dependent selects (options computed from other values): snap to the first valid option.
    for (const f of fields) {
      if (f.type !== 'select' || typeof f.options !== 'function') continue
      const ids = f.options(next).map((o) => (typeof o === 'string' ? o : o.id))
      if (!ids.includes(next[f.name])) next[f.name] = ids[0]
    }
    return next
  })

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const out = {}
      for (const f of fields) {
        let v = values[f.name]
        if (f.type === 'number') v = v === '' || v == null ? null : Number(v)
        if (f.type === 'checkbox') v = Boolean(v)
        if (typeof v === 'string') v = v.trim()
        out[f.name] = v === '' || v === undefined ? null : v
      }
      await onSubmit(out)
      onClose()
    } catch (e2) { setErr(e2.message); setBusy(false) }
  }

  const visible = fields.filter((f) => !f.hidden || !f.hidden(values))
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="row g-3">
        {visible.map((f) => (
          <div key={f.name} className={f.col || 'col-12'}>
            {f.type === 'checkbox' ? (
              <div className="form-check">
                <input id={f.name} type="checkbox" className="form-check-input" checked={Boolean(values[f.name])} onChange={(e) => set(f.name, e.target.checked)} />
                <label htmlFor={f.name} className="form-check-label">{f.label}</label>
              </div>
            ) : (
              <>
                <label htmlFor={f.name} className="form-label small">{f.label}</label>
                {f.type === 'select' ? (
                  <select id={f.name} className="form-select" value={values[f.name] ?? ''} required={f.required} onChange={(e) => set(f.name, e.target.value)}>
                    {!f.required && <option value="">—</option>}
                    {(typeof f.options === 'function' ? f.options(values) : f.options).map((o) => {
                      const [v, l] = typeof o === 'string' ? [o, o] : [o.id, o.label]
                      return <option key={v} value={v}>{l}</option>
                    })}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea id={f.name} className="form-control" rows={2} value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} />
                ) : (
                  <input id={f.name} type={f.type || 'text'} step={f.step || (f.type === 'number' ? 'any' : undefined)} className="form-control"
                    value={values[f.name] ?? ''} required={f.required} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} />
                )}
                {f.help && <div className="form-text">{f.help}</div>}
              </>
            )}
          </div>
        ))}
        {err && <div className="col-12"><div className="alert alert-danger py-2 mb-0">{err}</div></div>}
        <div className="col-12 d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        </div>
      </form>
    </Modal>
  )
}

export function RowActions({ onEdit, onDelete }) {
  const [confirm, setConfirm] = useState(false)
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3000); return () => clearTimeout(t) }, [confirm])
  return (
    <span className="d-inline-flex gap-1 align-items-center">
      {onEdit && <button className="icon-btn" onClick={onEdit} title="Edit"><PencilSquare /></button>}
      {onDelete && (confirm
        ? <><button className="btn btn-danger btn-sm py-0" onClick={onDelete}>Delete</button><button className="icon-btn" onClick={() => setConfirm(false)}><X /></button></>
        : <button className="icon-btn" onClick={() => setConfirm(true)} title="Delete"><Trash3 /></button>)}
    </span>
  )
}

export function Amount({ value, type, sign }) {
  const tone = type === 'income' ? 'pos' : type === 'expense' ? 'neg' : value > 0 ? '' : value < 0 ? 'neg' : ''
  const v = type === 'expense' ? -Math.abs(value) : value
  const digits = Number.isInteger(Number(value)) ? 0 : 2
  return <span className={`num ${tone}`}>{money(v, { sign: sign ?? type === 'income', digits })}</span>
}

/** Number input that saves on blur/Enter (spreadsheet-style editing). */
export function CommitInput({ value, onSave, className = 'form-control form-control-sm text-end' }) {
  const [v, setV] = useState(value ?? '')
  const [prev, setPrev] = useState(value)
  if (value !== prev) { setPrev(value); setV(value ?? '') }
  const commit = () => { const n = Number(v); if (v !== '' && Number.isFinite(n) && n !== value) onSave(n) }
  return (
    <input type="number" step="0.01" className={className} style={{ fontVariantNumeric: 'tabular-nums' }} value={v}
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />
  )
}
